import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { DateTime, Interval } from 'luxon';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import SmallHeader from '../Van/singleVanPage/SmallHeader';
import Footer from 'src/components/Footer';
import { useActions } from 'src/hooks/useActions';
import { useTypedSelector } from 'src/hooks/useTypeSelector';
import { dineroFormat, dineroSubtractPrice } from 'src/common/utils/dataFormat';
import Icons from 'src/common/icons';
import './styles.scss';

const _item = (title: string, links?: string | string[]) => (
  <div className='client__order__container__documents__item'>
    <div className='client__order__container__documents__title'>{`${title} : `}</div>
    {Array.isArray(links) ? (
      links.map((link, index) => (
        <Tooltip title={title} key={index}>
          <a
            href={link}
            target='_blank'
            className='client__order__container__documents__data icon_stroke'
          >
            <Icons.LilPic />
          </a>
        </Tooltip>
      ))
    ) : links ? (
      <Tooltip title={title}>
        <a
          href={links}
          target='_blank'
          className='client__order__container__documents__data icon_stroke'
        >
          <Icons.LilPic />
        </a>
      </Tooltip>
    ) : (
      'missing'
    )}
  </div>
);

const ClientPage: FC = () => {
  const { getClient, payNow } = useActions();
  const navigate = useNavigate();
  const {
    client: { client, orderList, isLoading, result },
  } = useTypedSelector((state) => state);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const { id } = jwtDecode(token!) as { [key: string]: any };
    getClient(id);
  }, [token]);

  useEffect(() => {
    result.pay &&
      window.open(`${result!.pay}`, '_blank', 'noopener,noreferrer');
  }, [result.pay]);

  return (
    <>
      <div className='header'>
        <SmallHeader />
      </div>
      <div className='client'>
        {isLoading.get ? (
          <div className='loading-container'>
            <CircularProgress size={100} />
          </div>
        ) : (
          <>
            {orderList ? (
              <div className='client__orders'>
                {orderList.map((order) => (
                  <div className='client__order'>
                    <h4 className='client__order__title'>
                      Order #{order.id}
                    </h4>
                    <div className='client__order__main'>
                      <div className='client__order__main__info'>
                        <h5>
                          <b>Status:</b>
                          {order.status}
                        </h5>
                        <h5>
                          <b>Booked:</b>
                          {
                            DateTime.fromISO(order.created_at, { zone: "UTC" })
                              .plus({ minutes: order.timeOffset }).toFormat("ff")
                          }
                        </h5>
                        <h5>
                          <b>From:</b>
                          {
                            `${DateTime.fromISO(order.start_date, { zone: "UTC" })
                              .plus({ minutes: order.timeOffset }).toFormat("ff")} 
                            | ${order.location} ${order.sub_location ? ` | ${order.sub_location}` : ''
                            }`
                          }
                        </h5>
                        <h5>
                          <b>To:</b>
                          {
                            `${DateTime.fromISO(order.end_date, { zone: "UTC" })
                              .plus({ minutes: order.timeOffset }).toFormat("ff")} 
                            | ${order.end_location || order.location} ${order.sub_location_end ? ` | ${order.sub_location_end}` : ''
                            }`
                          }
                        </h5>
                        {order.delivery_address && (
                          <>
                            <h5>
                              <b>Deliver to:</b>
                              {order.delivery_address}
                            </h5>
                            <h5>
                              <b>Return at:</b>
                              {order.return_at ? order.return_at : order.delivery_address && "same as delivery"}
                            </h5>
                          </>
                        )}
                        <h5>
                          <b>Host Email:</b>{order.van?.host.email}
                        </h5>
                        <h5>
                          <b>Host Phone:</b>{order.van?.host.phone}
                        </h5>
                      </div>
                      <div className='client__order__main__van'>
                        <img src={order.van!.photos?.exterior[0]} alt='van' />
                      </div>
                    </div>
                    <div className='client__order__container'>
                      <div className='client__order__container__payment'>
                        <h4>Payments</h4>
                        <div className='client__order__container__payment__main'>
                          <div className='client__order__container__payment__items'>
                            <h5>
                              <b>Partial:</b>
                              <span className='green'>
                                {dineroFormat(order.partial_paid, order.currency)}
                              </span>
                            </h5>
                            <h5>
                              <b>Balance:</b>
                              <span className='red'>
                                {dineroSubtractPrice(order.price, order.partial_paid, order.currency)}
                              </span>
                            </h5>
                            <h5>
                              <b>Total:</b>
                              <span className='blue'>
                                {dineroFormat(order.price, order.currency)}
                              </span>
                            </h5>
                          </div>
                          <Button
                            onClick={() => payNow(order.id)}
                            children={isLoading.pay ? <CircularProgress size={15} /> : 'Pay Now'}
                          />
                        </div>
                      </div>
                      <div className='client__order__container__drivers'>
                        <h4>Additional Drivers</h4>
                        <div className='client__order__container__drivers__items'>
                          <div className='client__order__container__drivers__item'>
                            {order.additional_driver.map((driver, index) => (
                              <div
                                key={index}
                                className='client__order__container__drivers__item__title'
                              >{`${driver.first_name} ${driver.last_name}`}</div>
                            ))}
                          </div>
                          <Button
                            onClick={() => navigate(`/driver/add/${order.inviteDriverHash}`)}
                            children='Invite Drivers'
                          />
                        </div>
                      </div>
                      <div className='client__order__container__documents'>
                        <h4>Documents</h4>
                        <div className='client__order__container__documents__main'>
                          <div className='client__order__container__documents__items'>
                            {_item(
                              'Drivers license',
                              client!.driver_license?.photos as string[] | undefined
                            )}
                            {_item(
                              'Insurance card',
                              client!.driver_license?.insurance as string | undefined
                            )}
                            {_item('Signed contract', order.pdf_order)}
                          </div>
                          <div className='client__order__container__documents__main__actions'>
                            <Button
                              onClick={() => navigate(`/license/${order.licenseHash}`)}
                              children='Upload DL'
                            />
                            <Button
                              onClick={() => navigate(`/contract/${order.signRaHash}`)}
                              children='Sign RA'
                              disabled={
                                (order.pdf_order ||
                                  Interval.fromDateTimes(
                                    DateTime.now(),
                                    DateTime.fromISO(orderList![0].start_date)
                                  ).toDuration(["days"]).days > 3
                                ) ? true : false}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>
      <div className='footer'>
        <Footer />
      </div>
    </>
  );
};

export default ClientPage;
